import appApi from "@/api/appApi";
import { dayjs } from "@/core/services/dayjs";

window.dayjs = dayjs;

const _statuses = {
  "1": "in_future",
  "2": "waiting_for_opponent",
  "3": "started",
  "4": "score_confirmed",
  "5": "ended",
  "6": "protest",
  "7": "technical_loose",
  "8": "technical_win",
  "9": "to_be_defined",
  "10": "two_winners"
};

const _statusDescriptions = {
  in_future: {
    type: "Предстоящий матч",
    status: "Матч состоится:"
  },
  waiting_for_opponent: {
    type: "Ожидается оппонент",
    status: "Матч состоится:"
  },
  score_confirmed: {
    type: "Счёт подтверждён",
    status: "Матч состоялся:"
  },
  ended: {
    type: "Прошедший матч",
    status: "Матч состоялся:"
  },
  technical_loose: {
    type: "Тех. поражение",
    status: "Матч состоялся:"
  },
  technical_win: {
    type: "Тех. победа",
    status: "Матч состоялся:"
  },
  two_winners: {
    type: "Победители квалификации",
    status: "Матч состоялся:"
  }
};

const _statusGroups = {
  live: ["started", "protest"],
  ended: ["ended", "technical_loose", "technical_win", "score_confirmed"],
  can_protest: ["waiting_for_opponent", "started"]
};

export default {
  getUserSlot({ player1 = {}, player2 = {} }, user = {}) {
    return Object.entries({ player1, player2 })
      .map(([key, player]) => {
        return player && user && player.id === user.id ? key : null;
      })
      .join("");
  },

  getStatus(status) {
    return _statuses[status] || status;
  },

  getStatusDescription(status) {
    let s = this.getStatus(status);
    return _statusDescriptions[s] || s;
  },

  getStatusGroup(status) {
    let s = _statuses[status] || status;
    let groups = Object.keys(_statusGroups);
    return groups.filter(g => _statusGroups[g].includes(s));
  },

  getConfirmationSideStatus(
    match = {},
    confirmExpired = false,
    playerSide = ""
  ) {
    const status = this.getStatus(match.status);
    const { player1 = {}, player2 = {} } = match;
    const player1_confirmation = (player1 && player1.score_confirmation) || 0;
    const player2_confirmation = (player2 && player2.score_confirmation) || 0;
    const hasAnyConfirmation = player1_confirmation + player2_confirmation > 0;

    if (status == "protest") {
      // Если есть хотябы одно подтверждения счета и время таймаута НЕ ИСТЕКЛО
      if (!confirmExpired && hasAnyConfirmation) {
        return "scores_are_different"; // указан разный счет
      }
      // Если есть хотябы одно подтверждения счета и время таймаута ИСТЕКЛО
      if (confirmExpired && hasAnyConfirmation) {
        return "score_not_confirmed"; // счет не был подтвержден одной из сторон
      }
      // Иначе протест был вызван вручную
      return "invited_referee";
    } else if (status == "started") {
      if (match[playerSide] && hasAnyConfirmation && !confirmExpired) {
        let { score_confirmation } = match[playerSide];
        if (score_confirmation === 1) return "confirmed_score_side";
        if (score_confirmation === 0) return "should_confirm_score_side";
      }
    }

    return null;
  },

  checkIsConfirmationExpired(match) {
    if (!match.confirmation_timeout) return false;
    return dayjs.unix(match.confirmation_timeout).isBefore(dayjs());
  },

  detectSecondStatus(match, user, isReferee) {
    const playerSide = isReferee ? "referee" : this.getUserSlot(match, user);
    const confirmExpired = this.checkIsConfirmationExpired(match);

    let confStatus = this.getConfirmationSideStatus(
      match,
      confirmExpired,
      playerSide
    );

    if (confStatus) return confStatus;

    return null;
  },

  // CONFIRM SCORE
  async getScore(id = 0) {
    const { data } = await appApi.get("/match/score", {
      params: { id }
    });

    return data;
  },

  async sendScore(id = 0, scores = {}) {
    const { data } = await appApi.post(
      "/match/confirm-score",
      {},
      {
        params: { id, ...scores }
      }
    );

    return data;
  },

  // SET PROTEST
  async inviteReferee(id = 0) {
    const { data } = await appApi.get("/match/request-referee", {
      params: { id }
    });

    return data;
  },

  async promoteTeam(id = 0, side) {
    if (!id || ![1, 2].includes(side))
      return {
        success: false,
        message: "Wrong request"
      };
    const { data } = await appApi.get("/match/promote", {
      params: { id, side }
    });

    return data;
  },

  async setTechLoose(id = 0) {
    const { data } = await appApi.get("/match/set-tech-loose", {
      params: { id }
    });

    return data;
  },

  // CHAT
  async getAttachment(id = 0) {
    const { data } = await appApi.get("/match/attachment", {
      params: { id }
    });

    return data;
  },

  async uploadFile(id = 0, body = {}, opts = {}) {
    const { data } = await appApi.post("/match/upload-attachment", body, {
      ...opts,
      params: { id }
    });
    return data;
  },

  // FETCH DATA
  async fetchProtestedMatches() {
    try {
      const { data } = await appApi.get("/match/protest-list");
      return data;
    } catch {
      return [];
    }
  },

  async fetchActiveMatches() {
    try {
      const { data } = await appApi.get("/match/active-list");
      return data;
    } catch {
      return [];
    }
  },

  async fetchMatch(id = 0) {
    const { data } = await appApi.get("/match/view", {
      params: { id }
    });

    return data;
  },

  async checkParticipant(id = 0) {
    const { success } = await appApi
      .get("/match/is-participant", {
        params: { id }
      })
      .then(({ data }) => data);

    return success;
  },

  async pickHero(matchId, heroId) {
    const params = {
      id: matchId,
      hero_id: heroId
    };

    const { data } = await appApi.get("/match/pick-ban", { params });
    return data;
  },

  async resetPickBans(matchId) {
    const params = { id: matchId };

    const { data } = await appApi.get("/match/reset-pick-ban", { params });
    return data;
  }
};
